import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

export type FeedbackRating = 'positive' | 'negative'

export type FeedbackSubmission = {
  rating: FeedbackRating
  reason?: string
  cause?: string
  comment?: string
}

export type FeedbackState =
  | { kind: 'idle' }
  | { kind: 'submitting'; rating: FeedbackRating }
  | { kind: 'submitted'; rating: FeedbackRating }
  | { kind: 'error'; message: string }

export type UseFeedbackOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
}

export type UseFeedbackResult = {
  state: FeedbackState
  submit: (interactionId: string, submission: FeedbackSubmission) => Promise<void>
  reset: () => void
}

export function useFeedback(options: UseFeedbackOptions): UseFeedbackResult {
  const [state, setState] = useState<FeedbackState>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)

  const api = useAnswerApi(options)

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    [],
  )

  const submit = useCallback(
    async (interactionId: string, submission: FeedbackSubmission) => {
      const controller = new AbortController()
      abortRef.current?.abort()
      abortRef.current = controller
      setState({ kind: 'submitting', rating: submission.rating })
      try {
        await api.feedback(
          {
            feedbackRequest: {
              interactionId,
              rating: submission.rating,
              reason: submission.reason ?? null,
              cause: submission.cause ?? null,
              comment: submission.comment ?? null,
            },
          },
          { signal: controller.signal },
        )
        if (controller.signal.aborted) return
        setState({ kind: 'submitted', rating: submission.rating })
      } catch (err) {
        if (controller.signal.aborted) return
        const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
        setState({ kind: 'error', message: apiErr.message })
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
      }
    },
    [api],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setState({ kind: 'idle' })
  }, [])

  return { state, submit, reset }
}
